import { Copy, FileText, Gavel, History, Info, MessageSquareMore, Play, RefreshCw, Send, ShieldAlert, XCircle } from 'lucide-react';
import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { api, isApiError, type DisclosureCase, type DisclosureDecisionRequest, type DisclosureReleaseResult } from '@/api';
import { Callout, DataTable, Field, Section, type Column } from '@/components/blocks';
import { DataError } from '@/components/DataError';
import { EntityRef } from '@/components/EntityRef';
import { DomainLink, PlainId } from '@/components/Ids';
import { KeyValue } from '@/components/KeyValue';
import { PageHeader, useDocumentTitle } from '@/components/PageHeader';
import { CopyText, EmptyState, Loading } from '@/components/States';
import { useRetained, useWrite, WriteError } from '@/components/Write';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { formatDateTime, formatRelative } from '@/lib/format';
import { useApi } from '@/lib/useApi';
import { ActionDialog, ConfirmCheck, DueCell, EnumBadge, FieldChips, FormActions, MfaHint, Timeline, useEnumLabel, useNow } from './ui';
import { DISCLOSURE_STATUS } from './vocab';
import { policyLabel } from '@/lib/labels';

type Dialogs = 'info' | 'decide' | 'release' | 'close' | null;
const TABS = ['summary', 'evidence', 'actions', 'history'] as const;
type Tab = (typeof TABS)[number];
type Release = DisclosureCase['releases'][number];

function TextActionForm({
  label,
  submitLabel,
  send,
  onDone,
  onCancel,
}: {
  label: string;
  submitLabel: string;
  send: (text: string) => Promise<unknown>;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const [text, setText] = useState('');
  const write = useWrite((v: string) => send(v));
  return (
    <form
      className="grid gap-4"
      onSubmit={async (e) => {
        e.preventDefault();
        if (text.trim() && (await write.run(text.trim()))) onDone();
      }}
    >
      <Field label={label}>
        <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} disabled={write.busy} />
      </Field>
      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
      <FormActions>
        <Button type="button" variant="outline" onClick={onCancel} disabled={write.busy}>
          {tc('action.cancel')}
        </Button>
        <Button type="submit" disabled={write.busy || !text.trim()}>
          {write.busy ? t('common.saving') : submitLabel}
        </Button>
      </FormActions>
    </form>
  );
}

function DecideForm({ c, onDone, onCancel }: { c: DisclosureCase; onDone: () => void; onCancel: () => void }) {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const [approved, setApproved] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState(false);
  const write = useWrite((req: DisclosureDecisionRequest, opts) => api.decideDisclosureCase(c.id, req, opts));
  const deny = approved.length === 0;
  const outcome = deny ? t('disclosure.decide.deny') : approved.length === c.requested_fields.length ? t('disclosure.decide.approveAll') : t('disclosure.decide.approvePartial');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!reason.trim() || !confirm) return;
    if (await write.run({ approved_fields: approved, reason: reason.trim() })) onDone();
  };

  return (
    <form onSubmit={submit} className="grid gap-4">
      <Callout tone="info">{t('disclosure.decide.fourEyes')}</Callout>
      <fieldset className="grid gap-2">
        <legend className="mb-1 text-xs font-medium text-muted-foreground">{t('disclosure.decide.fields')}</legend>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {c.requested_fields.map((f) => (
            // eslint-disable-next-line jsx-a11y/label-has-associated-control -- the checkbox is the child
            <label key={f} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={approved.includes(f)}
                onCheckedChange={() => setApproved(approved.includes(f) ? approved.filter((x) => x !== f) : [...approved, f])}
                disabled={write.busy}
              />
              <code className="text-xs">{f}</code>
            </label>
          ))}
        </div>
      </fieldset>
      <Field label={t('disclosure.decide.reason')}>
        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} disabled={write.busy} />
      </Field>
      <ConfirmCheck checked={confirm} onChange={setConfirm} disabled={write.busy}>
        {t('disclosure.decide.confirm')}
      </ConfirmCheck>
      <MfaHint permission="disclosure.decide" />
      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
      <FormActions>
        <Button type="button" variant="outline" onClick={onCancel} disabled={write.busy}>
          {tc('action.cancel')}
        </Button>
        <Button type="submit" variant={deny ? 'destructive' : 'default'} disabled={write.busy || !reason.trim() || !confirm}>
          {write.busy ? t('common.saving') : outcome}
        </Button>
      </FormActions>
    </form>
  );
}

function ReleaseForm({ c, onReleased, onCancel }: { c: DisclosureCase; onReleased: (r: DisclosureReleaseResult) => void; onCancel: () => void }) {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const [channel, setChannel] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // Deliberately not `useWrite`: nothing about the released data should outlive this call here.
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!channel.trim() || !confirm) return;
    setBusy(true);
    setError(null);
    try {
      onReleased(await api.releaseDisclosureCase(c.id, channel.trim()));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-4">
      <FieldChips fields={c.approved_fields} />
      <Field label={t('disclosure.release.channel')}>
        <Input value={channel} onChange={(e) => setChannel(e.target.value)} disabled={busy} />
      </Field>
      <ConfirmCheck checked={confirm} onChange={setConfirm} disabled={busy}>
        {t('disclosure.release.confirm')}
      </ConfirmCheck>
      <MfaHint permission="disclosure.release" />
      {error ? <WriteError error={error} /> : null}
      <FormActions>
        <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>
          {tc('action.cancel')}
        </Button>
        <Button type="submit" disabled={busy || !channel.trim() || !confirm}>
          <Send /> {busy ? t('disclosure.release.busy') : t('disclosure.release.submit')}
        </Button>
      </FormActions>
    </form>
  );
}

/**
 * The released registration data, shown once. It lives only in page state: not
 * in the URL, storage, or any cache; it disappears on "Done", on navigation, and
 * after 10 minutes.
 */
function ReleasedData({ r, onDismiss }: { r: DisclosureReleaseResult; onDismiss: () => void }) {
  const { t } = useTranslation('compliance');
  useEffect(() => {
    const timer = window.setTimeout(onDismiss, 10 * 60_000);
    return () => window.clearTimeout(timer);
  }, [onDismiss]);
  const json = JSON.stringify(r.data, null, 2);
  return (
    <section className="space-y-4 rounded-xl border-2 border-critical/40 bg-card p-5 shadow-sm" aria-labelledby="release-title">
      <div role="alert" className="flex gap-3 rounded-lg border border-critical/30 bg-critical-soft p-3 text-sm">
        <ShieldAlert className="mt-0.5 size-5 shrink-0 text-critical" aria-hidden="true" />
        <div className="space-y-1">
          <strong id="release-title" className="font-semibold text-critical">
            {t('disclosure.released.title')}
          </strong>
          <p>{t('disclosure.released.body')}</p>
        </div>
      </div>
      <KeyValue
        columns={2}
        rows={Object.entries(r.data).map(
          ([k, v]) =>
            [
              <code key={k}>{k}</code>,
              v === null ? (
                <span key={`${k}-v`} className="text-muted-foreground">
                  {t('disclosure.released.notOnRecord')}
                </span>
              ) : (
                String(typeof v === 'object' ? JSON.stringify(v) : v)
              ),
            ] as const,
        )}
      />
      <p className="flex flex-wrap items-center gap-2 text-sm">
        {t('disclosure.released.sha')} <CopyText value={r.payload_sha256} display={`${r.payload_sha256.slice(0, 16)}…`} />
      </p>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => navigator.clipboard?.writeText(json).catch(() => undefined)}>
          <Copy /> {t('disclosure.released.copy')}
        </Button>
        <Button type="button" size="sm" onClick={onDismiss}>
          {t('disclosure.released.done')}
        </Button>
      </div>
    </section>
  );
}

function NextAction({
  icon,
  title,
  onClick,
  variant = 'outline',
  disabled,
}: {
  icon: ReactNode;
  title: string;
  onClick: () => void;
  variant?: 'outline' | 'default' | 'destructive';
  disabled?: boolean;
}) {
  return (
    <Button type="button" variant={variant} onClick={onClick} disabled={disabled}>
      {icon} {title}
    </Button>
  );
}

/** `/compliance/icann/disclosure/:id` */
export function DisclosureCasePage() {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const label = useEnumLabel();
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const result = useApi((signal) => api.getDisclosureCase(id, { signal }), [id]);
  const c = useRetained(result.status === 'success' ? result.data : undefined);
  const [dialog, setDialog] = useState<Dialogs>(null);
  const [released, setReleased] = useState<DisclosureReleaseResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const dismissRelease = useCallback(() => setReleased(null), []);
  const now = useNow();
  const rawTab = params.get('tab');
  const tab: Tab = (TABS as readonly string[]).includes(rawTab ?? '') ? (rawTab as Tab) : 'summary';
  useDocumentTitle(c ? c.case_number : t('disclosure.detail.title'));
  const crumbs = [
    { label: t('crumbs.compliance') },
    { label: t('crumbs.icann') },
    { label: t('disclosure.list.title'), to: '/compliance/icann/disclosure' },
    { label: c?.case_number ?? '…' },
  ];

  if (!c) {
    return (
      <>
        <PageHeader title={t('disclosure.detail.title')} crumbs={crumbs} />
        {result.status === 'error' ? (
          <DataError error={result.error} onRetry={isApiError(result.error) && result.error.status === 404 ? undefined : result.reload} title={t('disclosure.detail.loadError')} />
        ) : (
          <Loading rows={4} />
        )}
      </>
    );
  }

  const done = () => {
    setDialog(null);
    result.reload();
  };
  const setTab = (next: string) => {
    const p = new URLSearchParams(params);
    if (next === 'summary') p.delete('tab');
    else p.set('tab', next);
    setParams(p, { replace: true });
  };
  const startReview = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.startDisclosureReview(c.id);
      done();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  const s = c.status;
  const open = s === 'RECEIVED' || s === 'UNDER_REVIEW' || s === 'INFO_REQUESTED' || s === 'APPROVED' || s === 'PARTIALLY_APPROVED';
  const overdue = open && Boolean(c.deadline_at) && new Date(c.deadline_at ?? 0).getTime() < now;

  const releaseCols: Column<Release>[] = [
    {
      key: 'at',
      header: t('disclosure.detail.col.released'),
      cell: (r) => <span className="whitespace-nowrap">{formatDateTime(r.released_at)}</span>,
    },
    {
      key: 'fields',
      header: t('disclosure.detail.col.fields'),
      cell: (r) => <FieldChips fields={r.fields} />,
    },
    {
      key: 'channel',
      header: t('disclosure.detail.col.channel'),
      cell: (r) => r.delivery_channel,
    },
    {
      key: 'by',
      header: t('disclosure.detail.col.by'),
      cell: (r) => <EntityRef label={r.released_by_display_name ?? label('actorType', r.released_by_type)} id={r.released_by_id} />,
    },
    {
      key: 'sha',
      header: t('disclosure.detail.col.sha'),
      cell: (r) => <CopyText value={r.payload_sha256} display={`${r.payload_sha256.slice(0, 12)}…`} />,
    },
  ];

  const actions = (
    <div className="flex flex-wrap gap-2">
      {s === 'RECEIVED' || s === 'INFO_REQUESTED' ? (
        <NextAction
          icon={<Play />}
          title={s === 'INFO_REQUESTED' ? t('disclosure.detail.resumeReview') : t('disclosure.detail.startReview')}
          onClick={startReview}
          variant="default"
          disabled={busy}
        />
      ) : null}
      {s === 'UNDER_REVIEW' ? (
        <>
          <NextAction icon={<Gavel />} title={t('disclosure.detail.decide')} onClick={() => setDialog('decide')} variant="default" />
          <NextAction icon={<MessageSquareMore />} title={t('disclosure.detail.requestInfo')} onClick={() => setDialog('info')} />
        </>
      ) : null}
      {s === 'APPROVED' || s === 'PARTIALLY_APPROVED' ? (
        <NextAction icon={<Send />} title={t('disclosure.detail.release')} onClick={() => setDialog('release')} variant="default" />
      ) : null}
      {s === 'RECEIVED' || s === 'INFO_REQUESTED' || s === 'DENIED' || s === 'RELEASED' ? (
        <NextAction icon={<XCircle />} title={t('disclosure.detail.close')} onClick={() => setDialog('close')} />
      ) : null}
    </div>
  );

  const history = [
    {
      key: 'received',
      at: c.received_at,
      title: t('disclosure.detail.history.received'),
      tone: 'info' as const,
    },
    c.decided_at
      ? {
          key: 'decided',
          at: c.decided_at,
          title: t('disclosure.detail.history.decided'),
          tone: 'pending' as const,
          meta: c.decision_reason,
        }
      : null,
    ...c.releases.map((r) => ({
      key: r.id,
      at: r.released_at,
      title: t('disclosure.detail.history.released', {
        channel: r.delivery_channel,
      }),
      tone: 'ok' as const,
      meta: null,
    })),
  ]
    .filter((x) => x !== null)
    .sort((a, b) => b.at.localeCompare(a.at));

  return (
    <>
      <PageHeader
        title={<span className="font-mono">{c.case_number}</span>}
        crumbs={crumbs}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <EnumBadge group="disclosureStatus" value={c.status} tones={DISCLOSURE_STATUS} />
            <span title={c.requester_type}>{label('requesterType', c.requester_type)}</span>
            <span aria-hidden="true">·</span>
            <DomainLink keyOrName={c.domain_id ?? c.domain_name} label={c.domain_name} />
          </span>
        }
        actions={
          <Button size="sm" variant="outline" onClick={result.reload}>
            <RefreshCw /> {tc('action.refresh')}
          </Button>
        }
      />
      <div className="space-y-4">
        {params.get('nopolicy') === '1' || (!c.deadline_at && !c.policy_key) ? <Callout tone="warn">{t('disclosure.detail.noPolicy')}</Callout> : null}
        {overdue && c.deadline_at ? (
          <Callout tone="crit" title={t('sla.overdue')}>
            {t('disclosure.detail.overdue', {
              when: formatRelative(c.deadline_at),
            })}
          </Callout>
        ) : null}
        {released ? <ReleasedData r={released} onDismiss={dismissRelease} /> : null}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-auto flex-wrap">
            <TabsTrigger value="summary">
              <Info /> {t('common.tabs.summary')}
            </TabsTrigger>
            <TabsTrigger value="evidence">
              <FileText /> {t('common.tabs.evidence')}
            </TabsTrigger>
            <TabsTrigger value="actions">
              <Gavel /> {t('common.tabs.actions')}
            </TabsTrigger>
            <TabsTrigger value="history">
              <History /> {t('common.tabs.history')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="summary" className="mt-2 space-y-4">
            {s !== 'CLOSED' ? (
              <Section title={t('disclosure.detail.nextStep')}>
                {actions}
                {error ? (
                  <div className="mt-3">
                    <WriteError error={error} />
                  </div>
                ) : null}
              </Section>
            ) : null}
            <Section title={t('disclosure.detail.request')}>
              <KeyValue
                columns={2}
                rows={[
                  [t('disclosure.detail.received'), formatDateTime(c.received_at)],
                  [
                    t('disclosure.detail.deadline'),
                    c.deadline_at ? <DueCell key="d" dueAt={c.deadline_at} overdue={overdue} done={open ? undefined : formatDateTime(c.deadline_at)} /> : '—',
                  ],
                  [t('disclosure.detail.legalBasis'), c.legal_basis],
                  [t('disclosure.detail.purpose'), c.purpose],
                  [t('disclosure.detail.requestedFields'), <FieldChips key="rf" fields={c.requested_fields} approved={c.decided_at ? c.approved_fields : undefined} />],
                  [
                    t('disclosure.detail.policy'),
                    c.policy_key ? (
                      <EntityRef key="p" label={policyLabel(c.policy_key)} to={`/compliance/policies/${encodeURIComponent(c.policy_key)}`} sub={c.policy_version ?? undefined} />
                    ) : (
                      '—'
                    ),
                  ],
                  [t('disclosure.detail.registeredBy'), <EntityRef key="cb" label={c.created_by_display_name} id={c.created_by} />],
                ]}
              />
            </Section>
            <div className="grid gap-4 lg:grid-cols-2">
              <Section title={t('disclosure.detail.requesterSection')}>
                <KeyValue
                  rows={[
                    [t('disclosure.detail.requester'), `${c.requester_name}${c.requester_organization ? `, ${c.requester_organization}` : ''}`],
                    [t('disclosure.detail.requesterEmail'), <code key="re">{c.requester_email_hint}</code>],
                  ]}
                />
              </Section>
              <Section title={t('disclosure.detail.decisionSection')}>
                <KeyValue
                  rows={[
                    [t('disclosure.detail.decision'), c.decided_at ? `${formatDateTime(c.decided_at)}: ${c.decision_reason ?? ''}` : '—'],
                    [t('disclosure.detail.reviewer'), <EntityRef key="rv" label={c.reviewer_display_name} id={c.reviewer_id} />],
                  ]}
                />
              </Section>
            </div>
          </TabsContent>

          <TabsContent value="evidence" className="mt-2">
            <Section title={t('disclosure.detail.documents')} description={t('disclosure.detail.evidenceHint')}>
              {c.supporting_document_ids.length ? (
                <ul className="flex flex-wrap gap-2">
                  {c.supporting_document_ids.map((d) => (
                    <li key={d}>
                      <PlainId id={d} />
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState title={t('disclosure.detail.noDocuments')} />
              )}
            </Section>
          </TabsContent>

          <TabsContent value="actions" className="mt-2 space-y-4">
            <Section title={t('disclosure.detail.nextStep')}>
              {s === 'CLOSED' ? <p className="text-sm text-muted-foreground">{t('disclosure.detail.noAction')}</p> : actions}
              {error ? (
                <div className="mt-3">
                  <WriteError error={error} />
                </div>
              ) : null}
              <MfaHint className="mt-4" />
            </Section>
            <Section title={t('disclosure.detail.releaseLog')} description={t('disclosure.detail.releaseLogNote')} flush>
              <DataTable columns={releaseCols} rows={c.releases} rowKey={(r) => r.id} empty={<EmptyState title={t('disclosure.detail.nothingReleased')} />} />
            </Section>
          </TabsContent>

          <TabsContent value="history" className="mt-2">
            <Section title={t('common.tabs.history')}>
              <Timeline
                items={history.map((h) => ({
                  key: h.key,
                  at: h.at,
                  title: <span className="font-medium">{h.title}</span>,
                  meta: h.meta ?? undefined,
                  tone: h.tone,
                }))}
              />
            </Section>
          </TabsContent>
        </Tabs>
      </div>

      <ActionDialog open={dialog === 'info'} onOpenChange={(o) => setDialog(o ? 'info' : null)} title={t('disclosure.info.title')}>
        <TextActionForm
          label={t('disclosure.info.label')}
          submitLabel={t('disclosure.info.submit')}
          send={(v) => api.requestDisclosureInfo(c.id, v)}
          onDone={done}
          onCancel={() => setDialog(null)}
        />
      </ActionDialog>
      <ActionDialog
        open={dialog === 'decide'}
        onOpenChange={(o) => setDialog(o ? 'decide' : null)}
        title={t('disclosure.decide.title')}
        description={t('disclosure.decide.description')}
        wide
      >
        <DecideForm c={c} onDone={done} onCancel={() => setDialog(null)} />
      </ActionDialog>
      <ActionDialog
        open={dialog === 'release'}
        onOpenChange={(o) => setDialog(o ? 'release' : null)}
        title={t('disclosure.release.title')}
        description={t('disclosure.release.description', {
          count: c.approved_fields.length,
        })}
      >
        <ReleaseForm
          c={c}
          onCancel={() => setDialog(null)}
          onReleased={(r) => {
            setReleased(r);
            done();
          }}
        />
      </ActionDialog>
      <ActionDialog open={dialog === 'close'} onOpenChange={(o) => setDialog(o ? 'close' : null)} title={t('disclosure.closeForm.title')}>
        <TextActionForm
          label={t('disclosure.closeForm.label')}
          submitLabel={t('disclosure.closeForm.submit')}
          send={(v) => api.closeDisclosureCase(c.id, v)}
          onDone={done}
          onCancel={() => setDialog(null)}
        />
      </ActionDialog>
    </>
  );
}
